import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Input, Spinner } from 'reactstrap';
import { useHistory, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import * as Sentry from '@sentry/react';

import { ROUTES } from 'config/constants';
import { Autocomplete } from 'containers/App/components/Header/Autocomplete';

import { getAutocompleteProducts } from 'api/products';

import searchIcon from 'assets/images/search-normal.png';

import './Search.scss';

const SearchInput = ({
    searchQuery,
    isMobile
}) => {
    const history = useHistory();
    const location = useLocation();
    const containerRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [autoCompleteProducts, setAutoCompleteProducts] = useState([]);
    const [searchHistoryVisibile, setSearchHistoryVisibile] = useState(false);
    const [isAutocompleteVisibile, setAutocompleteVisibility] = useState(false);
    const [searchHistory, setSearchHistory] = useState([]);
    const [search, setSearch] = useState(searchQuery);

    const handleClickOutside = (event) => {
        if (
            containerRef.current &&
            !containerRef.current.contains(event.target)
        ) {
            resetSearch();
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        const searchHistory = JSON.parse(localStorage.getItem('searchHistory')) || [];

        if (searchHistory.length > 0) setSearchHistory(searchHistory);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (window.location.pathname === ROUTES.SEARCH && (searchHistory && searchHistory.length !== 0)) {
            setSearchHistoryVisibile(true);
        }
    }, [searchHistory]);

    useEffect(() => {
        handleUrlChange();
        const unlisten = history.listen(handleUrlChange);

        return () => unlisten();
    }, [location.pathname]);

    const handleUrlChange = () => setAutocompleteVisibility(false);

    useEffect(() => {
        if (isAutocompleteVisibile) {
            updateTheLocalSearchHistory(search);
            setSearchHistoryVisibile(false);
        } else {
            setSearchHistory(JSON.parse(localStorage.getItem('searchHistory')));
        }
    }, [isAutocompleteVisibile]);

    useEffect(() => {
        if (search !== undefined) populateProductsWithSearchFilling(search);
    }, [search]);

    const updateTheLocalSearchHistory = (search) => {
        const searchHistory = JSON.parse(localStorage.getItem('searchHistory')) || [];
        const updatedHistory = [search, ...searchHistory.filter(item => item !== search)];

        if (!searchHistory.includes(search)) {
            searchHistory.push(search);
            localStorage.setItem('searchHistory', JSON.stringify(searchHistory));
        }

        if (updatedHistory.length > 5) {
            updatedHistory.pop();
        }

        localStorage.setItem('searchHistory', JSON.stringify(updatedHistory));
        ReactGA.event({
            category: 'Search',
            action: 'User Completed Search',
            label: search
        });
    };

    const populateProductsWithSearchFilling = async (value) => {
        setLoading(true);

        try {
            if (value && value.length >= 3) {
                const prods = await getAutocompleteProducts(value);

                if (prods?.data.length > 0) setAutoCompleteProducts(prods?.data);
                setTimeout(() => {
                    setAutocompleteVisibility(true);
                    setSearchHistoryVisibile(false);
                }, 1000);
            } else {
                if (value && value.length === 0) {
                    resetSearch();
                }
            }
        } catch (error) {
            Sentry.captureException(error);
        } finally {
            setLoading(false);
        }
    };

    const clearSearchHistory = () => {
        localStorage.removeItem('searchHistory');
        setSearchHistoryVisibile(false);
    };

    const resetSearch = () => {
        setSearch('');
        setAutoCompleteProducts([]);

        if (isMobile) {
            setSearchHistoryVisibile(true);
        } else {
            setSearchHistoryVisibile(false);
        }

        setAutocompleteVisibility(false);
    };

    const handleSearchOnFocus = () => {
        const searchHistory = JSON.parse(localStorage.getItem('searchHistory')) || [];

        if (searchHistory.length > 0) setSearchHistoryVisibile(true);
    };

    const handleSearch = async e => {
        setAutoCompleteProducts([]);
        const { value } = e.target;

        setSearch(value);
        populateProductsWithSearchFilling(value);
    };

    const fillSearchInput = (item) => setSearch(item);

    const rememberTheLastSearchItem = (item) => {
        updateTheLocalSearchHistory(item?.name);

        if (isMobile) {
            history.push(ROUTES.PRODUCT.generate(item?.slug, null));
        } else {
            window.location.href = ROUTES.PRODUCT.generate(item?.slug, null);
        }
    };

    const navigateToSearchScreen = () => history.push(ROUTES.SEARCH);

    if (isMobile && window.location.pathname !== ROUTES.SEARCH) {
        return <img className='searchIcon' alt='Search Icon' src={searchIcon}
            onClick={navigateToSearchScreen} />;
    }

    return (
        <div className='searchContainer_' ref={containerRef}>
            <div className='searchInputGroup'>
                <img className='searchIcon' alt='Search Icon' src={searchIcon} />
                <Input
                    type='text'
                    name='search'
                    value={search}
                    autoComplete='off'
                    onChange={handleSearch}
                    onFocus={handleSearchOnFocus}
                    onClick={handleSearchOnFocus}
                    placeholder='Search for device' />
                {loading && <Spinner className='spinner' />}
            </div>
            {isAutocompleteVisibile
                ? <Autocomplete
                    productsList={autoCompleteProducts}
                    rememberTheLastSearchItem={rememberTheLastSearchItem}
                    closeList={resetSearch} />
                : searchHistoryVisibile && <SearchHistory searchHistory={searchHistory} clearSearchHistory={clearSearchHistory} fillSearchInput={fillSearchInput} />
            }
        </div>
    );
};

const SearchHistory = ({
    searchHistory,
    clearSearchHistory,
    fillSearchInput
}) => {
    return (
        <div className='searchHistory'>
            <div className='searchHistory_header'>
                <span>Search History</span>
                <button onClick={clearSearchHistory}>Clear All</button>
            </div>
            <div className='searchHistory_results'>
                {searchHistory && searchHistory.map(item => <span key={item} onClick={() => fillSearchInput(item)}>{item}</span>)}
            </div>
        </div>
    );
};

SearchHistory.propTypes = {
    searchHistory: PropTypes.array,
    clearSearchHistory: PropTypes.func,
    fillSearchInput: PropTypes.func
};

SearchInput.propTypes = {
    searchQuery: PropTypes.string,
    isMobile: PropTypes.bool,
    rememberTheLastSearchItem: PropTypes.func
};

export default SearchInput;
